import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Smartphone, Monitor, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { useMobileSubscription } from '@/hooks/use-mobile-subscription';
import { useToast } from '@/hooks/use-toast';
import { Capacitor } from '@capacitor/core';
import { apiClient } from '@/lib/api-client';
import type { SubscriptionPlatform, SubscriptionStatus } from '@/lib/types';

interface SubscriptionManagementProps {
  className?: string;
}

export function SubscriptionManagement({ className }: SubscriptionManagementProps) {
  const { isSubscribed, user } = useAuth();
  const {
    currentSubscription,
    openSubscriptionManagement,
    getSubscriptionManagementUrl,
    checkSubscriptionStatus
  } = useMobileSubscription(user?.uid);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isServerSubscriptionLoading, setIsServerSubscriptionLoading] = useState(false);
  const [serverSubscription, setServerSubscription] = useState<ServerSubscriptionInfo | null>(null);

  const isNativePlatform = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();

  useEffect(() => {
    let isMounted = true;

    const fetchServerSubscription = async () => {
      if (!user?.uid) {
        if (isMounted) {
          setServerSubscription(null);
        }
        return;
      }

      setIsServerSubscriptionLoading(true);
      try {
        const response = await apiClient.getSubscriptionStatus();
        if (!isMounted) {
          return;
        }

        if (response.success && response.data) {
          console.log('[SubscriptionManagement] Subscription status response:', response.data);
          const {
            isActive = false,
            platform: subscriptionPlatform,
            status,
            productId,
            purchaseDate,
            expirationDate,
            lastValidated
          } = response.data as SubscriptionStatusResponse;

          // Always set server subscription if we have data, even if cancelled/expired
          // This allows us to properly check the status
          if (status !== undefined || subscriptionPlatform || isActive) {
            setServerSubscription({
              isActive,
              platform: subscriptionPlatform,
              status,
              productId,
              purchaseDate: parseDate(purchaseDate),
              expirationDate: parseDate(expirationDate),
              lastValidated: parseDate(lastValidated)
            });
          } else {
            setServerSubscription(null);
          }
        } else {
          setServerSubscription(null);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
        console.log('[SubscriptionManagement] Error detail:', error);
        if (isMounted) {
          setServerSubscription(null);
        }
      } finally {
        if (isMounted) {
          setIsServerSubscriptionLoading(false);
        }
      }
    };

    fetchServerSubscription();

    return () => {
      isMounted = false;
    };
  }, [user?.uid, isSubscribed]); // Refresh when subscription status changes

  const handleCancelSubscription = async () => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to manage your subscription.',
        variant: 'destructive'
      });
      return;
    }

    if (isNativePlatform) {
      // For mobile platforms, open the store's subscription management
      try {
        await openSubscriptionManagement();
        toast({
          title: 'Opening Subscription Management',
          description: 'Please cancel your subscription in the store settings.',
        });
      } catch (error) {
        console.error('Error opening subscription management:', error);
        toast({
          title: 'Error',
          description: 'Unable to open subscription management. Please try again.',
          variant: 'destructive'
        });
      }
    } else {
      // For web platform, use server-side cancellation
      try {
        setIsLoading(true);
        const response = await apiClient.cancelSubscription();
        if (response.success) {
          toast({
            title: 'Subscription Cancelled',
            description: 'Your subscription has been cancelled successfully.',
          });
          // Refresh subscription status
          await checkSubscriptionStatus();
        } else {
          toast({
            title: 'Cancellation Failed',
            description: response.error || 'Please try again.',
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        toast({
          title: 'Error',
          description: 'An error occurred while cancelling your subscription.',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const serverPlatform = serverSubscription?.platform;
  // Check if subscription is actually active (not cancelled)
  const serverSubscriptionActive = Boolean(
    serverSubscription?.isActive &&
    serverSubscription?.status !== 'cancelled' &&
    serverSubscription?.status !== 'expired'
  );
  const serverIndicatesActive = Boolean(serverSubscriptionActive || serverSubscription?.status === 'active');
  const normalizedCurrentNativePlatform = (isNativePlatform && (platform === 'ios' || platform === 'android')) ? platform as SubscriptionPlatform : null;
  const hasCrossPlatformConflict = Boolean(
    isNativePlatform &&
    serverIndicatesActive &&
    serverPlatform &&
    (serverPlatform === 'ios' || serverPlatform === 'android') &&
    normalizedCurrentNativePlatform &&
    serverPlatform !== normalizedCurrentNativePlatform
  );

  const getPlatformInstructions = (targetPlatform: SubscriptionPlatform | null | undefined) => {
    if (targetPlatform === 'ios') {
      return {
        title: 'Cancel on iOS',
        description: 'To cancel your subscription, you need to go to your Apple ID settings.',
        steps: [
          '1. Open Settings on your iPhone/iPad',
          '2. Tap your name at the top',
          '3. Tap "Subscriptions"',
          '4. Find "LullabAI" and tap "Cancel Subscription"'
        ]
      };
    } else if (targetPlatform === 'android') {
      return {
        title: 'Cancel on Android',
        description: 'To cancel your subscription, you need to go to Google Play Store.',
        steps: [
          '1. Open Google Play Store',
          '2. Tap your profile picture',
          '3. Tap "Payments & subscriptions"',
          '4. Tap "Subscriptions"',
          '5. Find "LullabAI" and tap "Cancel"'
        ]
      };
    }

    return null;
  };

  const instructions = getPlatformInstructions(platform as SubscriptionPlatform);
  const originalPlatformInstructions = getPlatformInstructions(serverPlatform);

  useEffect(() => {
    console.log('[SubscriptionManagement] Derived state', {
      isNativePlatform,
      platform,
      normalizedCurrentNativePlatform,
      isSubscribed,
      serverSubscription,
      serverIndicatesActive,
      serverSubscriptionActive,
      hasCrossPlatformConflict
    });
  }, [isNativePlatform, platform, normalizedCurrentNativePlatform, isSubscribed, serverSubscription, serverIndicatesActive, serverSubscriptionActive, hasCrossPlatformConflict]);

  // Only show card if there's actually an active subscription (not cancelled/expired)
  // Priority: isSubscribed from auth context is the primary indicator
  const isSubscriptionCancelledOrExpired = serverSubscription?.status === 'cancelled' || serverSubscription?.status === 'expired';

  // Show card if:
  // 1. User is subscribed (client-side check - primary indicator), OR
  // 2. There's a cross-platform conflict with an active subscription (not cancelled)
  // Don't show if subscription is cancelled/expired or if user is not subscribed (unless cross-platform conflict)
  const shouldShowCard = !isSubscriptionCancelledOrExpired && (
    isSubscribed ||
    (hasCrossPlatformConflict && serverSubscriptionActive)
  );
  if (!shouldShowCard) {
    return null;
  }

  const serverPlatformDisplayName = getPlatformDisplayName(serverPlatform);
  const currentPlatformDisplayName = getPlatformDisplayName(normalizedCurrentNativePlatform ?? (isNativePlatform ? platform : 'web'));
  const serverManageUrl = getPlatformManageUrl(serverPlatform);

  const detectedProductId = currentSubscription?.productId || serverSubscription?.productId;
  const MONTHLY_IDS = ['com.lullabai.app.monthly', 'com.lullabai.app.premium'];

  const planName = detectedProductId && MONTHLY_IDS.includes(detectedProductId)
    ? 'Monthly Plan'
    : detectedProductId
      ? 'Active Subscription'
      : null;

  const showActiveSubscriptionBlock = isSubscribed && currentSubscription;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isNativePlatform ? <Smartphone className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
          Subscription Management
        </CardTitle>
        <CardDescription>
          Manage your LullabAI subscription
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isServerSubscriptionLoading && !isSubscribed && (
          <div className="text-sm text-muted-foreground">
            Checking your subscription status...
          </div>
        )}

        {/* Current Subscription Status */}
        {showActiveSubscriptionBlock && currentSubscription && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-green-800">Active Subscription</h4>
                <p className="text-sm text-green-700">
                  {planName ?? 'Unknown Plan'}
                </p>
                {currentSubscription.expirationDate && (
                  <p className="text-sm text-green-700">
                    Expires: {currentSubscription.expirationDate.toLocaleDateString()}
                  </p>
                )}
              </div>
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Active
              </Badge>
            </div>
          </div>
        )}

        {/* Cross-platform conflict notice */}
        {hasCrossPlatformConflict && serverSubscription && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="space-y-2">
                <h4 className="font-semibold text-amber-800">
                  Subscription Purchased On {serverPlatformDisplayName}
                </h4>
                <p className="text-sm text-amber-700">
                  We found an active subscription linked to your account that was purchased on {serverPlatformDisplayName}.
                  To use full features on this device, cancel the existing subscription on {serverPlatformDisplayName} and then subscribe again on this {currentPlatformDisplayName}.
                </p>
                {planName && (
                  <p className="text-xs text-amber-700">
                    Detected plan: {planName}.
                  </p>
                )}
                {serverSubscription.expirationDate && (
                  <p className="text-xs text-amber-700">
                    Current access renews on {formatDisplayDate(serverSubscription.expirationDate)}.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Platform-specific instructions */}
        {isNativePlatform && !hasCrossPlatformConflict && instructions && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">{instructions.title}</h4>
            <p className="text-sm text-blue-700 mb-3">{instructions.description}</p>
            <ol className="text-sm text-blue-700 space-y-1">
              {instructions.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        {/* Original platform instructions for cross-platform cases */}
        {hasCrossPlatformConflict && originalPlatformInstructions && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">{originalPlatformInstructions.title}</h4>
            <p className="text-sm text-blue-700 mb-3">
              {originalPlatformInstructions.description} Use the same store account you used when you first subscribed.
            </p>
            <ol className="text-sm text-blue-700 space-y-1">
              {originalPlatformInstructions.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        {/* Cancel Subscription Button */}
        {isNativePlatform ? (
          hasCrossPlatformConflict ? (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                if (serverManageUrl) {
                  window.open(serverManageUrl, '_blank');
                }
              }}
              disabled={!serverManageUrl}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Manage Subscription on {serverPlatformDisplayName}
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Cancel Subscription
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will open your device's subscription management settings where you can cancel your subscription.
                    <br /><br />
                    You will retain access to all features until the end of your current billing period.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancelSubscription}
                    disabled={isLoading}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isLoading ? 'Processing...' : 'Open Settings'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )
        ) : (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3 text-sm text-yellow-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-1" />
              <div>
                <h4 className="font-semibold">Manage on Mobile</h4>
                <p>
                  Subscription management is only available in the LullabAI mobile apps. Please open the app on your iOS or Android device to manage or cancel your subscription.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                window.open('https://lullabai.dev', '_blank');
              }}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Download the Mobile App
            </Button>
          </div>
        )}

        {/* Additional Help */}
        {isNativePlatform && !hasCrossPlatformConflict && (
          <div className="text-center">
            <Button
              variant="outline"
              onClick={async () => {
                const url = await getSubscriptionManagementUrl();
                if (url) {
                  window.open(url, '_blank');
                }
              }}
              className="w-full"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Store Settings
            </Button>
          </div>
        )}

        <div className="text-xs text-muted-foreground text-center">
          {hasCrossPlatformConflict ? (
            <p>
              Your subscription is managed through your {serverPlatformDisplayName}. Cancel it there, then resubscribe on this {currentPlatformDisplayName} app to unlock all features here.
            </p>
          ) : isNativePlatform ? (
            <p>
              Subscriptions are managed through your {platform === 'ios' ? 'Apple ID' : 'Google Play'} account.
            </p>
          ) : (
            <p>
              You can manage your subscription directly from this app.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

type SubscriptionStatusResponse = {
  isActive?: boolean;
  platform?: SubscriptionPlatform;
  status?: SubscriptionStatus;
  productId?: string;
  purchaseDate?: unknown;
  expirationDate?: unknown;
  lastValidated?: unknown;
};

type SubscriptionDateFields = {
  purchaseDate?: Date | null;
  expirationDate?: Date | null;
  lastValidated?: Date | null;
};

type ServerSubscriptionInfo = {
  isActive: boolean;
  platform?: SubscriptionPlatform;
  status?: SubscriptionStatus;
  productId?: string;
} & SubscriptionDateFields;

function parseDate(value: unknown): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'object' && value !== null) {
    if ('toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
      try {
        return (value as { toDate: () => Date }).toDate();
      } catch {
        return null;
      }
    }

    const maybeSeconds = (value as { _seconds?: number })._seconds;
    if (typeof maybeSeconds === 'number') {
      const nanoseconds = (value as { _nanoseconds?: number })._nanoseconds ?? 0;
      return new Date(maybeSeconds * 1000 + nanoseconds / 1_000_000);
    }
  }

  return null;
}

function formatDisplayDate(date: Date | null | undefined): string | null {
  if (!date) {
    return null;
  }

  try {
    return date.toLocaleDateString();
  } catch {
    return null;
  }
}

function getPlatformDisplayName(platform: SubscriptionPlatform | string | null | undefined): string {
  switch (platform) {
    case 'ios':
      return 'Apple App Store';
    case 'android':
      return 'Google Play Store';
    case 'web':
      return 'Web';
    default:
      return 'Unknown Platform';
  }
}

function getPlatformManageUrl(platform: SubscriptionPlatform | null | undefined): string | null {
  switch (platform) {
    case 'ios':
      return 'https://apps.apple.com/account/subscriptions';
    case 'android':
      return 'https://play.google.com/store/account/subscriptions';
    default:
      return null;
  }
}
